import type { CSSProperties, JSX, ReactNode } from 'react';
import { Toggle } from './Toggle';
import { roleAtLeast, type ViewerTier } from './roles';
import type { AdminSection } from './types';

/** One admin-controlled feature flag (Sexton, a Corpus kill-switch, …). */
export interface FeatureFlagItem {
  key: string;
  label: string;
  description?: string;
  enabled: boolean;
  /** Optional per-flag gate (defaults to the section's minRole). */
  minRole?: ViewerTier;
}

export interface FeatureToggleSectionProps {
  flags: FeatureFlagItem[];
  onToggle: (key: string, next: boolean) => void | Promise<void>;
  viewer: ViewerTier;
  error?: string;
  onError?: (err: unknown) => void;
}

const muted: CSSProperties = {
  fontSize: 13,
  color: 'var(--color-muted, #6b7280)',
  margin: '8px 0',
};

export function FeatureToggleSection({
  flags,
  onToggle,
  viewer,
  error,
  onError,
}: FeatureToggleSectionProps): JSX.Element {
  const visible = flags.filter((f) => !f.minRole || roleAtLeast(viewer, f.minRole));
  return (
    <div>
      {error && <p style={{ ...muted, color: 'var(--color-danger, #b3261e)' }}>{error}</p>}
      {visible.length === 0 ? (
        <p style={muted}>No features to configure.</p>
      ) : (
        visible.map((f) => (
          <Toggle
            key={f.key}
            checked={f.enabled}
            label={f.label}
            {...(f.description ? { description: f.description } : {})}
            {...(onError ? { onError } : {})}
            onChange={(next) => onToggle(f.key, next)}
          />
        ))
      )}
    </div>
  );
}

/** Convenience factory → an AdminSection (Features, `admin`-tier by default). */
export function featureToggleSection(
  opts: Omit<FeatureToggleSectionProps, 'viewer'> & {
    key?: string;
    title?: string;
    icon?: ReactNode;
    minRole?: ViewerTier;
  },
): AdminSection {
  const { key, title, icon, minRole, ...body } = opts;
  return {
    key: key ?? 'features',
    title: title ?? 'Features',
    ...(icon ? { icon } : {}),
    minRole: minRole ?? 'admin',
    body: (ctx) => <FeatureToggleSection viewer={ctx.viewer} {...body} />,
  };
}
